import type { Href } from 'expo-router';
import { useState } from 'react';

import { useBoats } from '@todo/core/hooks/use-boats';
import { useEvents } from '@todo/core/hooks/use-events';
import { useTours } from '@todo/core/hooks/use-tours';
import {
  MIN_BOATS_PER_CITY_PAGE,
  boatTypePageOf,
  type BoatTypePage,
  type BoatTypePages,
} from '@/lib/boat-rental';
import type { Boat } from '@todo/core/boats';
import { sliceHasCityEventsPage, sliceHasCityToursPage } from '@todo/core/city-listings';
import { cities } from '@/lib/events-data';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  BOATS_INDEX_QUERY,
  BOATS_INDEX_SEED_KEY,
  BOAT_TYPE_PAGES_SEED_KEY,
  EVENTS_INDEX_QUERY,
  EVENTS_INDEX_SEED_KEY,
  LIST_BUCKET,
  TOURS_INDEX_QUERY,
  TOURS_INDEX_SEED_KEY,
  WINE_TOURS_INDEX_QUERY,
  WINE_TOURS_INDEX_SEED_KEY,
  cityBoatsQuery,
  cityBoatsSeedKey,
  cityEventsQuery,
  cityEventsSeedKey,
  cityToursQuery,
  cityToursSeedKey,
  crossSellBoatsQuery,
  crossSellBoatsSeedKey,
  crossSellToursQuery,
  crossSellToursSeedKey,
} from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';
import type { DbEvent } from '@todo/core/supabase';
import type { Tour } from '@todo/core/tours';
import { isWineTour, wineTourCityIds } from '@/lib/wine-tours';

// How many sibling cards the "more like this" block shows — one tidy row of
// fixed-width cards (see related-list.tsx, which sizes the block to 4 across).
// The reused seed carries more rows than this (24 for a city, 60/100
// region-wide); we slice for a compact below-the-fold block. Slicing is
// deterministic across the build and the client render, so it can't cause a
// hydration mismatch.
const RELATED_LIMIT = 4;

// Only the 7 known Riviera cities have a per-city build seed. An event/tour whose
// city_id is null or outside that set falls back to the region-wide index seed
// (also baked at export time), so every detail page still ships a populated,
// crawlable block instead of an empty one.
function knownCityId(cityId: string | null | undefined): string | null {
  return cityId && cities.some((c) => c.id === cityId) ? cityId : null;
}

export type RelatedEvents = {
  related: DbEvent[];
  relatedSeedKey: string;
  seeAllHref: Href;
  cityId: string | null;
};

/**
 * Upcoming events to show at the bottom of an event detail page. Reuses the
 * build-time list seed the city landing page already populates (city-events for
 * a known city, events-index region-wide) — so on web there's no extra fetch and
 * the cards are baked into the static HTML. Excludes the show being viewed.
 *
 * Always called (rules of hooks); returns an empty list until `event` resolves.
 */
export function useRelatedEvents(event: DbEvent | null): RelatedEvents {
  const { locale } = useLocale();
  const cityId = knownCityId(event?.city_id);
  const relatedSeedKey = cityId ? cityEventsSeedKey(cityId) : EVENTS_INDEX_SEED_KEY;
  const query = cityId ? cityEventsQuery(cityId) : EVENTS_INDEX_QUERY;
  const { events } = useEvents(query, relatedSeedKey);
  const related = event
    ? events.filter((e) => e.show_slug !== event.show_slug).slice(0, RELATED_LIMIT)
    : [];
  // The city's own /[city]/events page when it has one — decided from the
  // city seed itself, which can only undercount the page (see
  // sliceHasCityEventsPage), so the link never points at an unexported page.
  const seeAllHref: Href =
    cityId && sliceHasCityEventsPage(events)
      ? { pathname: '/[lang]/[city]/events', params: { lang: locale, city: cityId } }
      : { pathname: '/[lang]/events', params: { lang: locale } };
  return { related, relatedSeedKey, seeAllHref, cityId };
}

export type RelatedTours = {
  related: Tour[];
  relatedSeedKey: string;
  seeAllHref: Href;
  /** Set when every related card departs from this city — the heading names it. */
  cityId: string | null;
  /** True when the block lists wine tours only (the viewed tour is one). */
  wine: boolean;
};

/**
 * Top-rated tours to show at the bottom of a tour detail page. Mirrors
 * useRelatedEvents but over the tours seed (tours have no date, so the seed is
 * ordered by tour_rank_score — rating weighted by review count). Excludes the
 * tour being viewed.
 *
 * A wine tour gets wine tours instead (issue #37): the /wine-tours hub seed —
 * every wine tour in the region, already baked at export time — with the same
 * departure city first, then the rest of the region in rank order, so a city
 * with only a couple of wine tours still fills the row.
 */
export function useRelatedTours(tour: Tour | null): RelatedTours {
  const { locale } = useLocale();
  const cityId = knownCityId(tour?.city_id);
  const wine = tour ? isWineTour(tour) : false;
  const relatedSeedKey = wine
    ? WINE_TOURS_INDEX_SEED_KEY
    : cityId
      ? cityToursSeedKey(cityId)
      : TOURS_INDEX_SEED_KEY;
  const query = wine ? WINE_TOURS_INDEX_QUERY : cityId ? cityToursQuery(cityId) : TOURS_INDEX_QUERY;
  const { tours } = useTours(query, relatedSeedKey);

  if (wine) {
    const others = tours.filter((tr) => tr.id !== tour?.id);
    // Stable partition — keeps the RPC's rank order within each half.
    const related = [
      ...others.filter((tr) => cityId != null && tr.city_id === cityId),
      ...others.filter((tr) => cityId == null || tr.city_id !== cityId),
    ].slice(0, RELATED_LIMIT);
    // The city's /wine-tours/[city] page when it has one — decided from the
    // hub seed, which can only undercount a city (it's the whole theme up to
    // its limit), so the link never points at an unexported page.
    const seeAllHref: Href =
      cityId && wineTourCityIds(tours).includes(cityId)
        ? { pathname: '/[lang]/wine-tours/[city]', params: { lang: locale, city: cityId } }
        : { pathname: '/[lang]/wine-tours', params: { lang: locale } };
    const sameCity = related.length > 0 && related.every((tr) => tr.city_id === cityId);
    return { related, relatedSeedKey, seeAllHref, cityId: sameCity ? cityId : null, wine };
  }

  const related = tour
    ? tours.filter((tr) => tr.id !== tour.id).slice(0, RELATED_LIMIT)
    : [];
  // The city's own /[city]/tours page when it has one — same reasoning as
  // useRelatedEvents (exact here: the tours slice has no date window).
  const seeAllHref: Href =
    cityId && sliceHasCityToursPage(tours)
      ? { pathname: '/[lang]/[city]/tours', params: { lang: locale, city: cityId } }
      : { pathname: '/[lang]/tours', params: { lang: locale } };
  return { related, relatedSeedKey, seeAllHref, cityId, wine };
}

export type RelatedBoats = {
  related: Boat[];
  relatedSeedKey: string;
  seeAllHref: Href;
  cityId: string | null;
  /** Whether cityId has an exported /boat-rental/[city] page (the "see all" target + breadcrumb parent). */
  hasCityPage: boolean;
  /** The boat's /boat-rental/[city]/[type] page, when one was exported. */
  typePage: BoatTypePage | null;
};

/**
 * Top-rated boats to show at the bottom of a boat detail page. Mirrors
 * useRelatedTours over the boats seed. Excludes the boat being viewed.
 *
 * "See all" goes to the city's /boat-rental/[city] page when it has one. That's
 * decided from the city seed itself (the same MIN_BOATS_PER_CITY_PAGE floor
 * the export applies — the seed's 24-row slice can't hide a city above it), so
 * the build and the client agree and the link never points at an unexported page.
 */
export function useRelatedBoats(boat: Boat | null): RelatedBoats {
  const { locale } = useLocale();
  const cityId = knownCityId(boat?.city_id);
  const relatedSeedKey = cityId ? cityBoatsSeedKey(cityId) : BOATS_INDEX_SEED_KEY;
  const query = cityId ? cityBoatsQuery(cityId) : BOATS_INDEX_QUERY;
  const { boats } = useBoats(query, relatedSeedKey);
  const related = boat
    ? boats.filter((b) => b.id !== boat.id).slice(0, RELATED_LIMIT)
    : [];
  const hasCityPage = cityId != null && boats.length >= MIN_BOATS_PER_CITY_PAGE;
  const seeAllHref: Href = hasCityPage
    ? { pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: cityId } }
    : { pathname: '/[lang]/boats', params: { lang: locale } };
  // From the page list the export baked (BoatHead bakes it into every boat
  // page), so the link never names an unexported page. Native has no seed and
  // links none.
  const [typePages] = useState(() =>
    getPrefetched<BoatTypePages>(LIST_BUCKET, BOAT_TYPE_PAGES_SEED_KEY),
  );
  const ownType = boatTypePageOf(boat?.boat_type);
  const typePage =
    hasCityPage && cityId && ownType && typePages?.[cityId]?.includes(ownType) ? ownType : null;
  return { related, relatedSeedKey, seeAllHref, cityId, hasCityPage, typePage };
}

/** At most this many of the cross-sell cards are boats; tours fill the rest of the row. */
const CROSS_SELL_BOATS = 2;

export type CrossSellItem = { kind: 'tour'; tour: Tour } | { kind: 'boat'; boat: Boat };

export type CityCrossSell = {
  items: CrossSellItem[];
  /** The known city the cards come from; null → region-wide. */
  cityId: string | null;
  toursSeedKey: string;
  boatsSeedKey: string;
};

/**
 * Top tours and boats for a city — the "tours & boats in this city" block on
 * the event and article pages, which get almost all of the site's search
 * clicks while the affiliate catalogues rank far down. One row: up to
 * CROSS_SELL_BOATS boats, tours filling the rest, either side topping up when
 * the other runs short (Menton has few boats). Same ranking as every other
 * list, from the cross-sell seeds (baked by the block itself — see
 * CityCrossSell), so the build and hydration renders agree.
 */
export function useCityCrossSell(cityId: string | null | undefined): CityCrossSell {
  const known = knownCityId(cityId);
  const toursSeedKey = crossSellToursSeedKey(known);
  const boatsSeedKey = crossSellBoatsSeedKey(known);
  const { tours } = useTours(crossSellToursQuery(known), toursSeedKey);
  const { boats } = useBoats(crossSellBoatsQuery(known), boatsSeedKey);

  let boatCount = Math.min(CROSS_SELL_BOATS, boats.length);
  const tourCount = Math.min(RELATED_LIMIT - boatCount, tours.length);
  boatCount = Math.min(RELATED_LIMIT - tourCount, boats.length);
  const items: CrossSellItem[] = [
    ...tours.slice(0, tourCount).map((tour) => ({ kind: 'tour' as const, tour })),
    ...boats.slice(0, boatCount).map((boat) => ({ kind: 'boat' as const, boat })),
  ];
  return { items, cityId: known, toursSeedKey, boatsSeedKey };
}
